'use client';

import { cn } from '@shared/components/shadcn';
import type { ComponentProps } from 'react';
import { ContentSwitcher } from '../../../form-controls/ContentSwitcher/ContentSwitcher';
import type { Option } from '../../../form-controls/types';

type ResultsViewToggleProps = Omit<ComponentProps<'div'>, 'onChange'> & {
  options: Option<string>[];
  value: string;
  onChange: (value: string) => void;
};

/**
 * Segmented control for switching labelled results header views.
 * Reuses {@link ContentSwitcher}. Hidden when fewer than two options are supplied.
 * Full width up to 335px below xl, fixed 335px at xl+.
 */
function ResultsViewToggle({ options, value, onChange, className, ...props }: ResultsViewToggleProps) {
  if (options.length < 2) return null;

  const selectedOption = options.find(option => option.value === value) ?? options[0];

  return (
    <div
      className={cn('w-full max-w-[335px] xl:w-[335px] shrink-0 max-xl:mx-auto', className)}
      data-testid="results-view-toggle"
      data-track-container="Results View Toggle"
      {...props}
    >
      <ContentSwitcher
        options={options}
        value={selectedOption}
        onChange={option => onChange(option.value)}
        size="large"
        fullWidth
        buttonClassName="paragraph min-w-fit"
        className="max-xl:bg-background"
      />
    </div>
  );
}

export { ResultsViewToggle, type ResultsViewToggleProps };
